import { Component, OnInit, Input, Output, OnChanges, EventEmitter, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';

import * as d3 from 'd3';
import * as _ from 'lodash';
import * as Moment from 'moment-timezone';
import { extendMoment } from 'moment-range';
/** moment instance extended with timezone and range */
const moment = extendMoment(Moment);

/**
 * CalendarRangeDates Dialog Component
 * Generates a component that allows to select year and month or trimester in a dialog
 */
@Component({
  selector: 'app-calendar-range-dates-dialog-popup',
  templateUrl: './calendar-range-dates-dialog-popup.html'
})
export class CalendarRangeDatesDialogPopupComponent {
  /**
   * Constructor of component. Injects services
   * @param dialogRef Angular Material Dialog Reference
   * @param data Dialog data injected
   */
  constructor(private dialogRef: MatDialogRef<CalendarRangeDatesDialogPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any) { }

  /** Called when mat-select changes, set selected year */
  selectYear(matSelectChange) {
    const val = matSelectChange.value;
    this.data.selectedYear = val;
    if (val && +moment(this.data.realDate).format('Y') && val !== +moment(this.data.realDate).format('Y')) {
      if (this.data.which === 'start') {
        if (this.data.selectedMonth) {
          this.data.realDate = moment(val + this.data.selectedMonth.startDate).startOf('month').toISOString();
        } else {
          this.data.realDate = moment(val + '-01').startOf('month').toISOString();
        }
      } else {
        if (this.data.selectedMonth) {
          this.data.realDate = moment(val + this.data.selectedMonth.endDate).endOf('month').toISOString();
        } else {
          this.data.realDate = moment(val + '-12').endOf('month').toISOString();
        }
      }
    }
  }

  /** Close popup dialog */
  closeDialog(): void {
    this.dialogRef.close(this.data);
  }

  /** Checks if month should be disabled based on date range */
  isMonthDisabled(year: number, month: { name: string, startDate: string, endDate: string }) {
    if (!year) {
      return true;
    }
    const realDates = this.data.dateRange.filter(date => {
      return +moment(date.toString().slice(0, 10)).format('Y') === year;
    });

    if (realDates && realDates[0] && realDates.length < this.data.months.length) {
      const rangeOne = moment.range(moment(realDates[0].toString().slice(0, 10)),
        moment(realDates[realDates.length - 1].toString().slice(0, 10)));
      const rangeTwo = moment.range(moment(year + month.startDate).startOf('month'),
        moment(year + month.endDate).endOf('month'));

      return !rangeOne.intersect(rangeTwo);
    } else {
      return false;
    }
  }

  /** Called when month is selected from UI */
  selectMonth(month) {
    this.data.selectedMonth = month;
    if (this.data.which === 'start') {
      this.data.realDate = moment(this.data.selectedYear + this.data.selectedMonth.startDate).startOf('month').toISOString();
    } else {
      this.data.realDate = moment(this.data.selectedYear + this.data.selectedMonth.endDate).endOf('month').toISOString();
    }
  }
}

/**
 * CalendarRangeDatesDialogComponent:
 * Displays whole range selected.
 */
@Component({
  selector: 'app-calendar-range-dates-dialog',
  templateUrl: './calendar-range-dates-dialog.component.html',
  styleUrls: ['./calendar-range-dates-dialog.component.scss']
})
export class CalendarRangeDatesDialogComponent implements OnInit, OnChanges {
  /** Two-positions array that specified date range for component */
  @Input() dateRange: string[];
  /** Selected range to set component  */
  @Input() selectedRange: { start: string, end: string };
  /** Emitter when dateStart changes */
  @Output() dateStartChange = new EventEmitter<any>();
  /** Emitter when dateEnd changes */
  @Output() dateEndChange = new EventEmitter<any>();
  /** Keeps the quenatity of months between date range */
  monthsBetween: any;
  /** Keep the freuency of dates range (monthly, yearly) */
  frequency: string;
  /** years defined in date range */
  years: number[];
  /** Months definition with name, start date and end date */
  months: { name: string, startDate: string, endDate: string }[];
  /** FormControl to select start year */
  selectedYear: FormControl;
  /** FormControl to select end year */
  selectedYearEnd: FormControl;
  /** Selected start month definition */
  selectedMonth: { name: string, startDate: string, endDate: string };
  /** Selected end month definition */
  selectedMonthEnd: { name: string, startDate: string, endDate: string };
  /** Start date from selection */
  startDate: string;
  /** End date from selection */
  endDate: string;

  /**
   * Inits component, injects MatDialog, starts form controls
   * @param dialog Angular Material MatDialog injected
   */
  constructor(private dialog: MatDialog) {
    moment.locale('es-US');
    moment['tz'].setDefault('America/Mexico_City');
    this.selectedYear = new FormControl();
    this.selectedYearEnd = new FormControl();
  }

  /** Set default selected year, month and date for start and end based on date range */
  ngOnInit() {
    this.years = [];
    this.months = [];

    // Start year selected
    this.selectedYear.valueChanges.subscribe(val => {
      if (val && val !== (+moment(this.startDate).format('Y'))) {
        if (this.selectedMonth && this.selectedMonth.startDate && moment(val + this.selectedMonth.startDate).startOf('month').isValid()) {
          this.startDate = moment(val + this.selectedMonth.startDate).startOf('month').toISOString();
        } else if (this.selectedRange.start) {
          this.startDate = moment(val + '-' + moment(this.selectedRange.start).format('MM')).startOf('month').toISOString();
          this.findMonthStart();
        } else {
          this.startDate = moment(val + '-01').startOf('month').toISOString();
        }
        this.dateStartChange.emit(this.startDate);
        this.validateDates();
      }
    });
    // End year selected
    this.selectedYearEnd.valueChanges.subscribe(val => {
      if (val && val !== (+moment(this.endDate).format('Y'))) {
        if (this.selectedMonthEnd && this.selectedMonthEnd.endDate) {
          this.endDate = moment(val + this.selectedMonthEnd.endDate).endOf('month').toISOString();
        } else if (this.selectedRange.end) {
          this.endDate = moment(val + '-' + moment(this.selectedRange.end).format('MM')).endOf('month').toISOString();
          this.findMonthEnd();
        } else {
          this.endDate = moment(val + '-12-31').endOf('month').toISOString();
        }
        if (moment(this.endDate).toDate().getTime() < moment(this.startDate).toDate().getTime()) {
          this.validateDates();
        } else {
          this.dateEndChange.emit(this.endDate);
        }
      }
    });
  }

  /**
   * Truggered when @Input() param changes
   * @param changes Changes object
   */
  ngOnChanges(changes) {
    const vm = this;
    if (changes.hasOwnProperty('dateRange')) {
      if (this.dateRange.length > 0) {
        this.dateRange = this.dateRange.filter(d => moment(d).isValid());
        this.monthsBetween = d3.min(this.dateRange, function (date: string, index: number) {
          if (date && moment(date).isValid() && vm.dateRange[index + 1] && moment(vm.dateRange[index + 1]).isValid()) {
            return Math.floor(moment(vm.dateRange[index + 1]).diff(date, 'days') / 28);
          } else {
            return 99999999;
          }
        });

        this.years = _.uniq(this.dateRange.filter(o => {
          return moment(o.toString().slice(0, 10)).isValid();
        }).map(o => {
          return +moment(o.toString().slice(0, 10)).format('Y');
        }));

        if (this.monthsBetween > 11) {
          this.frequency = 'Anual';
          this.months = [];
        } else if (this.monthsBetween === 3) {
          this.frequency = 'Trimestral';
          this.months = [
            { name: 'Ene - Mar', startDate: '-01', endDate: '-03' },
            { name: 'Abr - Jun', startDate: '-04', endDate: '-06' },
            { name: 'Jul - Sep', startDate: '-07', endDate: '-09' },
            { name: 'Oct - Dic', startDate: '-10', endDate: '-12' },
          ];
        } else if (this.monthsBetween === 1) {
          this.frequency = 'Mensual';
          this.months = [
            { name: 'Ene', startDate: '-01', endDate: '-01' },
            { name: 'Feb', startDate: '-02', endDate: '-02' },
            { name: 'Mar', startDate: '-03', endDate: '-03' },
            { name: 'Abr', startDate: '-04', endDate: '-04' },
            { name: 'May', startDate: '-05', endDate: '-05' },
            { name: 'Jun', startDate: '-06', endDate: '-06' },
            { name: 'Jul', startDate: '-07', endDate: '-07' },
            { name: 'Ago', startDate: '-08', endDate: '-08' },
            { name: 'Sep', startDate: '-09', endDate: '-09' },
            { name: 'Oct', startDate: '-10', endDate: '-10' },
            { name: 'Nov', startDate: '-11', endDate: '-11' },
            { name: 'Dic', startDate: '-12', endDate: '-12' },
          ];
        }
        this.setDefaultValues();
      }
    }
    if (changes.hasOwnProperty('selectedRange')) {
      this.setDefaultValues();
    }
  }

  /** Open dialog to select date ranges */
  openDialog(which: string): void {
    const dialogRef = this.dialog.open(CalendarRangeDatesDialogPopupComponent, {
      panelClass: 'calendar-dialog',
      width: '250px',
      data: {
        which: which,
        dateRange: this.dateRange,
        months: this.months,
        years: this.years,
        selectedYear: _.cloneDeep(this['selectedYear' + (which === 'end' ? 'End' : '')].value),
        selectedMonth: _.cloneDeep(this['selectedMonth' + (which === 'end' ? 'End' : '')])
      }
    });
    d3.select('app-root').style('pointer-events', 'none');

    dialogRef.afterClosed().subscribe(result => {
      d3.select('app-root').style('pointer-events', null);
      if (result) {
        if (result.which === 'start') {
          this.selectedMonth = result.selectedMonth;
          this.selectedYear.setValue(result.selectedYear);
          if (result.realDate) {
            this.startDate = result.realDate;
          }
          this.selectedRange.start = this.startDate;
          if (this.validateDates()) {
            this.dateStartChange.emit(this.startDate);
          }
        }
        if (result.which === 'end') {
          this.selectedMonthEnd = result.selectedMonth;
          this.selectedYearEnd.setValue(result.selectedYear);
          if (result.realDate) {
            this.endDate = result.realDate;
          }
          this.selectedRange.end = this.endDate;
          if (this.validateDates()) {
            this.dateEndChange.emit(this.endDate);
          }
        }
      }
    });
  }

  /** Validate selected dates, end date should be after start date */
  validateDates(): boolean {
    if (moment(this.endDate).toDate().getTime() < moment(this.startDate).toDate().getTime()) {
      alert('La fecha final no puede ser anterior que la fecha inicial');
      this.selectedYearEnd.setValue(+moment(this.startDate).format('Y'));
      this.endDate = moment(this.startDate).endOf('month').toISOString();
      this.findMonthEnd();
      this.dateEndChange.emit(this.endDate);
      return false;
    } else {
      return true;
    }
  }

  /**
   * Called when month has changed, Emits change
   * @param month Month data to be set
   */
  selectMonth(month): void {
    this.selectedMonth = month;
    this.startDate = moment(this.selectedYear.value + this.selectedMonth.startDate).startOf('month').toISOString();
    this.dateStartChange.emit(this.startDate);
  }

  /**
   * Called when end month has changed. Emits change
   * @param month Month data to be set
   */
  selectMonthEnd(month): void {
    this.selectedMonthEnd = month;
    if (this.selectedYearEnd.value) {
      this.endDate = moment(this.selectedYearEnd.value + month.endDate).endOf('month').toISOString();
      this.dateEndChange.emit(this.endDate);
    }
  }

  /**
   * Parse date with selected format, only year (YYYY) or month name + year
   * @param date Date to parse as string
   */
  parseYearMonth(date): string {
    let text = '------ ----';
    if (date && moment(date).isValid() && _.includes(this.dateRange.map(d => moment(d).format('Y')), moment(date).format('Y'))) {
      if (this.months.length < 1) {
        text = moment(date).format('YYYY');
      } else {
        text = moment(date).format('MMMM YYYY');
        text = text[0].toUpperCase() + text.slice(1, undefined);
      }
      return text;
    } else {
      return text;
    }
  }

  /**
   * Check if current month is included in dateRange
   * @param year Selected year
   * @param month Current month
   */
  isMonthDisabled(year: number, month: { name: string, startDate: string, endDate: string }) {
    if (!year) {
      return true;
    }
    const realDates = this.dateRange.filter(date => {
      return +moment(date.toString().slice(0, 10)).format('Y') === year;
    });

    if (realDates && realDates[0] && realDates.length < this.months.length) {
      const rangeOne = moment.range(moment(realDates[0].toString().slice(0, 10)),
        moment(realDates[realDates.length - 1].toString().slice(0, 10)));
      const rangeTwo = moment.range(moment(year + month.startDate).startOf('month'),
        moment(year + month.endDate).endOf('month'));

      return !rangeOne.intersect(rangeTwo);
    } else {
      return false;
    }
  }

  /** Sets default values for startdate and end date */
  setDefaultValues(): void {
    if (this.selectedRange) {
      this.startDate = this.selectedRange.start;
      this.selectedYear.setValue(+moment(this.startDate).format('Y'));
      this.findMonthStart();

      if (this.selectedRange.end) {
        this.endDate = this.selectedRange.end;
        this.selectedYearEnd.setValue(+moment(this.endDate).format('Y'));
        this.findMonthEnd();
      }
    }
  }

  /** Find month object definition for selected date */
  findMonthStart(): void {
    if (this.months) {
      this.selectedMonth = _.find(this.months, function (month) {
        const range = moment.range(moment(this.selectedYear.value + month.startDate).startOf('month'),
          moment(this.selectedYear.value + month.endDate).endOf('month'));
        if (range.contains(moment(this.startDate))) {
          return true;
        } else {
          return false;
        }
      }.bind(this));
    }
  }

  /** Find month end object definition for selected end date */
  findMonthEnd(): void {
    if (this.months) {
      this.selectedMonthEnd = _.find(this.months, function (month) {
        const range = moment.range(moment(this.selectedYearEnd.value + month.startDate).startOf('month'),
          moment(this.selectedYearEnd.value + month.endDate).endOf('month'));
        if (range.contains(moment(this.endDate))) {
          return true;
        } else {
          return false;
        }
      }.bind(this));
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/components/calendar-range-dates-dialog/calendar-range-dates-dialog.component.ts